import React, { useState } from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const LessonModal = ({ lesson, onClose, onUpdate, onDelete, onComplete, onCancel }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    date: lesson?.date || '',
    start_time: lesson?.start_time || '',
    end_time: lesson?.end_time || '',
    notes: lesson?.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!lesson) return null;

  const timeSlots = [];
  for (let hour = 7; hour <= 20; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
  }

  const handleSave = async () => {
    setLoading(true);
    try {
      await onUpdate(lesson.id, editData);
      setIsEditing(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await onDelete(lesson.id);
      onClose();
    } catch (error) {
      alert('Errore nell\'eliminazione');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await onComplete(lesson.id);
      onClose();
    } catch (error) {
      alert('Errore');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      await onCancel(lesson.id);
      onClose();
    } catch (error) {
      alert('Errore');
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-indigo-100 text-indigo-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'scheduled': return 'Programmata';
      case 'completed': return 'Completata';
      case 'cancelled': return 'Annullata';
      default: return status;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-900">
            {isEditing ? 'Modifica Lezione' : 'Dettaglio Lezione'}
          </h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ✕
          </button>
        </div>

        <div className="p-4 space-y-4">
          {confirmDelete ? (
            <div className="bg-red-50 p-4 rounded-xl">
              <p className="text-red-800 font-medium mb-4">
                Sei sicuro di voler eliminare questa lezione? La lezione verrà rimborsata al cliente.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700"
                  disabled={loading}
                >
                  Annulla
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 py-2 rounded-lg bg-red-600 text-white"
                  disabled={loading}
                >
                  {loading ? 'Eliminazione...' : 'Elimina'}
                </button>
              </div>
            </div>
          ) : isEditing ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data</label>
                <input
                  type="date"
                  value={editData.date}
                  onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Orario Inizio</label>
                <select
                  value={editData.start_time}
                  onChange={(e) => {
                    const start = e.target.value;
                    const [h, m] = start.split(':');
                    const endH = parseInt(h) + 1;
                    setEditData({ 
                      ...editData, 
                      start_time: start,
                      end_time: `${endH.toString().padStart(2, '0')}:${m}`
                    });
                  }}
                  className="w-full p-3 border border-gray-200 rounded-xl"
                >
                  {timeSlots.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
                <textarea
                  value={editData.notes || ''}
                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-xl"
                  rows={3}
                  placeholder="Note opzionali..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold"
                  disabled={loading}
                >
                  Annulla
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 py-3 rounded-xl bg-indigo-600 text-white font-semibold"
                  disabled={loading}
                >
                  {loading ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                  {lesson.client_name?.split(' ').map(n => n[0]).join('') || '?'}
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{lesson.client_name || 'Cliente'}</div>
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusStyle(lesson.status)}`}>
                    {getStatusText(lesson.status)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-xl">
                  <div className="text-xs text-gray-500 mb-1">Data</div>
                  <div className="font-semibold capitalize">
                    {format(new Date(lesson.date), 'EEE d MMM yyyy', { locale: it })}
                  </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl">
                  <div className="text-xs text-gray-500 mb-1">Orario</div>
                  <div className="font-semibold">
                    {lesson.start_time} - {lesson.end_time}
                  </div>
                </div>
              </div>

              {lesson.notes && (
                <div className="p-3 bg-gray-50 rounded-xl">
                  <div className="text-xs text-gray-500 mb-1">Note</div>
                  <div className="text-gray-700">{lesson.notes}</div>
                </div>
              )}

              {lesson.status === 'scheduled' && (
                <div className="space-y-3 pt-2">
                  <div className="flex gap-3">
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex-1 py-3 rounded-xl bg-indigo-100 text-indigo-700 font-semibold flex items-center justify-center gap-2"
                      data-testid="edit-lesson-btn"
                    >
                      Modifica
                    </button>
                    <button
                      onClick={handleComplete}
                      className="flex-1 py-3 rounded-xl bg-green-100 text-green-700 font-semibold flex items-center justify-center gap-2"
                      disabled={loading}
                    >
                      Segna Completata
                    </button>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleCancel}
                      className="flex-1 py-3 rounded-xl bg-amber-100 text-amber-700 font-semibold"
                      disabled={loading}
                    >
                      Annulla e Rimborsa
                    </button>
                    <button
                      onClick={() => setConfirmDelete(true)}
                      className="flex-1 py-3 rounded-xl bg-red-100 text-red-700 font-semibold"
                    >
                      Elimina Lezione
                    </button>
                  </div>
                </div>
              )}

              {lesson.status !== 'scheduled' && (
                <button
                  onClick={onClose}
                  className="w-full py-3 rounded-xl bg-gray-100 text-gray-700 font-semibold"
                >
                  Chiudi
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LessonModal;
